import { useEffect, useState } from "react";

import {
  TextField,
  Select,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  ColumnSortEnum,
  Label,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, PagedResult } from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { getSchoolCandidateFilteredList } from "../../api/SchoolCandidate/SchoolCandidateApi";
import {
  ContentArea,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import {
  SchoolCandidate,
  SchoolCandidateSearch,
  SchoolCandidateSort,
} from "../../models/SchoolCandidate";
import {
  useGetInstitutionListQuery,
  useGetSchoolCandidateStatusListQuery,
  useGetSchoolYearCurrentQuery,
} from "../../queries/codeBookQuery";
import { FormatUtils, TableUtils } from "../../utils";

export function SchoolCandidateInstitutionList() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  //STORAGE KEY
  const storageKey = "SchoolCandidateInstitutionSearchTableState";
  const institutionId = Number.parseInt(id);
  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<SchoolCandidateSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        SchoolCandidateSort.FirstName,
        ColumnSortEnum.ASC,
        50
      )
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<SchoolCandidateSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER
  const createInitialSchoolCandidateSearch = (): SchoolCandidateSearch => {
    return {
      schoolYearId: schoolYear?.id,
      countyList: [],
      municipalityList: [],
      townList: [],
      streetList: [],
      houseNumberFrom: undefined,
      houseNumberTo: undefined,
      houseNumberSelector: undefined,
      lastNamePart: undefined,
      oib: undefined,
      institutionList: [{ id: institutionId, name: "" }],
      status: undefined,
      onlyWithNote: false,
      onlyWithAddReasonNotOnItitialMUPList: false,
      onlyWithAddReasonDelayPreviousYear: false,
      onlyWithTemporaryAddress: false,
      onlyUnassignedSchoolCandidates: false,
      onlyWithCancelReason: false,
    };
  };
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<SchoolCandidate>();
  const [tableData, setTableData] =
    useState<PagedResult<SchoolCandidate>>(defaultTableData);
  //SET SEARCH FILTERS
  const [schoolCandidateSearch, setSchoolCandidateSearch] =
    useState<SchoolCandidateSearch>(createInitialSchoolCandidateSearch());
  const [tableSearch, setTableSearch] = useState<SchoolCandidateSearch>(
    createInitialSchoolCandidateSearch()
  );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(false);
  //TABLE METADATA
  const tableMetadata: TableMetadata<SchoolCandidate> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "firstName",
        header:
          t("common.schoolCandidateModel.lastName") +
          "/" +
          t("common.schoolCandidateModel.firstName"),
        accessor: (item: SchoolCandidate) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName},&nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.oib}
              </div>
            </div>
          );
        },
        sortBy: SchoolCandidateSort.FirstName,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: SchoolCandidate) => {
          return (
            <div className={"text-nowrap text-right"}>
              {hrDate(item.dateOfBirth)}
            </div>
          );
        },
        sortBy: SchoolCandidateSort.DateOfBirth,
      },
      {
        columnKey: "permanentAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: SchoolCandidate) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.residence?.permanentAddress?.street}&nbsp;
                  {item.residence?.permanentAddress?.houseNumber},&nbsp;
                  {item.residence?.permanentAddress?.town}{" "}
                  {/*,&nbsp;{item.permanentAddress.municipality},&nbsp;{item.permanentAddress.county*/}
                </b>
              </div>
              {item.residence?.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.residence?.temporaryAddress.street}&nbsp;
                  {item.residence?.temporaryAddress.houseNumber},&nbsp;
                  {item.residence?.temporaryAddress.town}{" "}
                  {/*,&nbsp;{item.temporaryAddress.municipality},&nbsp;{item.temporaryAddress.county*/}
                </div>
              )}
            </>
          );
        },
        sortBy: SchoolCandidateSort.PermanentAddress,
      },
      {
        columnKey: "permanentAddressNote",
        header: t("common.schoolCandidateModel.permanentAddressNote"),
        accessor: "permanentAddressNote",
        sortBy: SchoolCandidateSort.PermanentAddressNote,
      },
      {
        columnKey: "status",
        header: t("common.schoolCandidateModel.status"),
        accessor: (item: SchoolCandidate) => {
          return (
            <>
              <Label
                color={FormatUtils.getSchoolCandidateSentStatusColor(
                  item.statusId
                )}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                {statuses?.find((v) => v.id === item.statusId)?.name}
              </Label>
            </>
          );
        },
        sortBy: SchoolCandidateSort.Status,
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<SchoolCandidateSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: initialTableState.page,
    paginationPageSize: initialTableState.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: initialTableState.order,
      sortBy: initialTableState.sortBy,
    },
  });
  //FILTER DATA
  const { data: institutions } = useGetInstitutionListQuery();
  const getSelectedInstitution = (): CodeBook => {
    let institution = institutions?.find((ins) => ins.id === institutionId);
    return institution
      ? { id: institution.id, name: institution.name }
      : { id: institutionId, name: "" };
  };
  const [selectedInstitution] = useState<CodeBook>(getSelectedInstitution());
  const { data: statuses } = useGetSchoolCandidateStatusListQuery();

  useEffect(() => {
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      schoolYearId: schoolYear?.id,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
    setTableSearch(newSchoolCandidateSearch);
    setClearFilter(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYear]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolCandidateSearch]);

  useEffect(() => {
    if (!schoolCandidateSearch?.schoolYearId) return;
    (async () => {
      var request = TableUtils.GetSearchRequest<SchoolCandidateSearch>(
        tableSearch,
        tableState
      );
      var itemList = await getSchoolCandidateFilteredList(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const goBack = () => {
    history.goBack();
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleStatusChange = (e: any) => {
    const status = Number.parseInt(e.target.value);
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      status: !isNaN(status) && status > 0 ? status : undefined,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  console.log("schoolCandidateSearch", schoolCandidateSearch);

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("navigation.schoolCandidateList")}
        showBack="show"
        onClickBack={goBack}
      ></ToolbarTop>
      <SearchCard
        onClickSearch={() => {
          setTableSearch(schoolCandidateSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setSchoolCandidateSearch(createInitialSchoolCandidateSearch());
          setTableSearch(createInitialSchoolCandidateSearch());
          search();
        }}
        isFilterChanged={filterChanged}
        hideDivider={true}
      >
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof schoolCandidateSearch>("institutionList")}
              value={selectedInstitution.name}
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.status")}
              name={nameof<typeof schoolCandidateSearch>("status")}
              value={schoolCandidateSearch.status}
              onChange={handleStatusChange}
              selectData={codeBookToSelectData(statuses)}
              emptyOption={{ value: 0, text: t("common.all") }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable tableProps={tableProps} />
    </ContentArea>
  );
}
